import { defineComponent, h, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import cytoscape from 'cytoscape';
export type SceneNode = { id: string; label: string; kind: string; file?: string | null };
export type SceneEdge = { from: string; to: string; kind: string; role?: string };
export const nodeColors: Record<string, string> = {
  object: '#69b3a2',
  record: '#55a6b5',
  dataset: '#729cc9',
  schema: '#a481cb',
  unit: '#9c9ede',
  chunk: '#c8b6e2',
  document: '#efbd65',
  source: '#e89778',
  statement: '#4b8cc9',
  hypothesis: '#a481cb',
  derivation: '#7aabc5',
  passage: '#a2bead',
  citation: '#d7ac55',
  retrieval: '#94a3b8',
};
export const kindLabels: Record<string, string> = {
  object: '来源信息',
  record: '数据记录',
  dataset: '数据集',
  schema: '类型定义',
  unit: '信息单元',
  chunk: '目录片段',
  document: '转换文本',
  source: '原始文件',
  statement: '回答',
  hypothesis: '条件推断',
  derivation: '推导',
  passage: '原文片段',
  citation: '引文',
  retrieval: '检索记录',
  directory: '目录',
  directory_group: '目录组',
  block: '内容块',
};
export const edgeLabel = (e: SceneEdge) =>
  e.role === 'subject'
    ? '主体'
    : e.role === 'object'
      ? '客体'
      : e.role ||
        (
          {
            DERIVED_FROM: '来源于',
            DISCOVERED_BY: '检索发现',
            REFERS_TO: '引用',
            USES: '依据',
            DERIVED_BY: '推导自',
            CHALLENGED_BY: '相反依据',
            CONDITION_USES: '条件依据',
            SOURCE_ROLE: '关联',
            DIRECTORY_CHILD: '目录包含',
            DIRECTORY_SUPPORT: '目录依据',
            UNIT_SOURCE_CHUNK: '对应原文片段',
            INSTANCE_OF: '符合定义',
            IN_DATASET: '属于数据集',
          } as Record<string, string>
        )[e.kind] ||
        e.kind;
const short = (s: string) => (Array.from(s).length > 22 ? Array.from(s).slice(0, 22).join('') + '…' : s);
/** Reusable graph scene. Backend decides membership/relations; this component
 * owns only camera, layout, selection and node positions. */
export const GraphScene = defineComponent({
  props: {
    nodes: { type: Array as () => SceneNode[], required: true },
    edges: { type: Array as () => SceneEdge[], required: true },
    focus: String,
    trace: Boolean,
    fitRequest: { type: Number, default: 0 },
  },
  emits: ['select', 'expand', 'edge'],
  setup(props, { emit }) {
    const container = ref<HTMLElement>(),
      version = ref(0),
      zoom = ref(1);
    let cy: cytoscape.Core | undefined, observer: ResizeObserver | undefined;
    let fittedRequest = props.fitRequest;
    const placementCursors = new Map<string, number>();
    function layout() {
      if (!cy) return;
      if (props.trace) {
        // Source chains read left-to-right. Long Chinese labels must not set
        // the vertical spacing of every level and shrink four nodes to dots.
        const incoming = new Set(props.edges.map(e=>e.to));
        const queue = props.nodes.filter(n=>!incoming.has(n.id)).map(n=>n.id);
        const depth = new Map(queue.map(id=>[id,0]));
        for (let i=0;i<queue.length;i++) for (const e of props.edges.filter(e=>e.from===queue[i])) {
          if (!depth.has(e.to)) { depth.set(e.to,depth.get(queue[i])!+1); queue.push(e.to); }
        }
        const levels = new Map<number,string[]>();
        for (const n of props.nodes) { const d=depth.get(n.id)??0; levels.set(d,[...(levels.get(d)??[]),n.id]); }
        for (const [d,ids] of levels) ids.forEach((id,i)=>cy!.getElementById(id).position({x:d*225,y:(i-(ids.length-1)/2)*125}));
      } else cy.layout({name: 'cose', animate: false, padding: 65,
        nodeRepulsion: () => 14000, idealEdgeLength: () => 160, nodeDimensionsIncludeLabels: true} as any).run();
      cy.fit(undefined, 60);
      if (cy.zoom() > 1.2) { cy.zoom(1.2); cy.center(); }
    }
    function sync() {
      if (!cy) return;
      const c = cy,
        old = new Set(c.nodes().map((n) => n.id())),
        wanted = new Set(props.nodes.map((n) => n.id));
      c.batch(() => {
        c.nodes()
          .filter((n) => !wanted.has(n.id()))
          .remove();
        for (const n of props.nodes) {
          const data = { ...n, label: short(n.label), fullLabel: n.label, color: nodeColors[n.kind] || '#bac5ce' };
          if (c.getElementById(n.id).length) c.getElementById(n.id).data(data);
          else c.add({ group: 'nodes', data });
        }
        const edgeIds = new Set<string>();
        for (const e of props.edges) {
          const id = 'edge:' + JSON.stringify([e.from, e.to, e.kind, e.role || '']);
          edgeIds.add(id);
          if (c.getElementById(e.from).length && c.getElementById(e.to).length && !c.getElementById(id).length)
            c.add({
              group: 'edges',
              data: { id, source: e.from, target: e.to, label: edgeLabel(e), kind: e.kind, relation: e },
            });
        }
        c.edges()
          .filter((e) => !edgeIds.has(e.id()))
          .remove();
      });
      const added = c.nodes().filter((n) => !old.has(n.id()));
      if (!old.size) {
        placementCursors.clear();
        layout();
      } else if (added.length) {
        const anchorId = props.focus || '';
        const anchor = c.getElementById(anchorId).position() || { x: 0, y: 0 };
        const count = added.length;
        let cursor = placementCursors.get(anchorId) ?? 0;
        const occupied = c.nodes().filter(n=>old.has(n.id())).map(n=>(n as cytoscape.NodeSingular).position());
        added.forEach(n => {
          // Continue the same anchor's rings across pages. Also avoid nodes
          // already placed by another expansion, preserving their positions.
          for (;;) {
            let ring=1,slot=cursor++;
            while(slot>=12*ring) { slot-=12*ring;ring++; }
            const angle=slot*2*Math.PI/(12*ring)+ring*0.13;
            const point={x:anchor.x+Math.cos(angle)*ring*150,y:anchor.y+Math.sin(angle)*ring*140};
            if(occupied.every(p=>Math.hypot(p.x-point.x,p.y-point.y)>=65)) {
              n.position(point); occupied.push(point); break;
            }
          }
        });
        placementCursors.set(anchorId,cursor);
        if (count > 25) c.fit(undefined, 60);
      }
      c.nodes().removeClass('focused');
      if (props.focus) c.getElementById(props.focus).addClass('focused');
      if (fittedRequest !== props.fitRequest) {
        c.fit(undefined,60);
        fittedRequest = props.fitRequest;
      }
      version.value++;
    }
    onMounted(() => {
      cy = cytoscape({
        container: container.value,
        elements: [],
        minZoom: 0.03,
        maxZoom: 3,
        wheelSensitivity: 0.2,
        boxSelectionEnabled: false,
        style: [
          {
            selector: 'node',
            style: {
              'background-color': 'data(color)',
              label: 'data(label)',
              'text-wrap': 'wrap',
              'text-overflow-wrap': 'anywhere',
              'text-max-width': '125px',
              'font-size': 12,
              'font-family': 'system-ui',
              'text-valign': 'bottom',
              'text-margin-y': 9,
              width: 48,
              height: 48,
              color: '#344255',
              'border-width': 2,
              'border-color': '#ffffff',
            },
          },
          {
            selector: 'node.focused, node:selected',
            style: { 'border-width': 4, 'border-color': '#276b98', 'overlay-opacity': 0.08 },
          },
          {
            selector: 'edge',
            style: {
              width: 1.5,
              'line-color': '#aab4c2',
              'target-arrow-color': '#aab4c2',
              'target-arrow-shape': 'triangle',
              'curve-style': 'bezier',
              label: 'data(label)',
              'font-size': 10,
              'text-rotation': 'autorotate',
              'text-background-color': '#f8fafc',
              'text-background-opacity': 0.9,
              'text-background-padding': '3px',
              color: '#64748b',
            },
          },
          {
            selector: 'edge:selected',
            style: { width: 3, 'line-color': '#3677aa', 'target-arrow-color': '#3677aa', color: '#205576' },
          },
          {
            selector: 'edge[kind="DERIVED_FROM"]',
            style: { 'line-color': '#b4a3cf', 'target-arrow-color': '#b4a3cf' },
          },
        ],
      });
      cy.on('tap', 'node', (e) => emit('select', e.target.id()));
      cy.on('dbltap', 'node', (e) => emit('expand', e.target.id()));
      cy.on('tap', 'edge', (e) => emit('edge', e.target.data('relation')));
      cy.on('pan zoom position layoutstop', () => {
        zoom.value = cy!.zoom();
        version.value++;
      });
      let wasHidden = true;
      observer = new ResizeObserver(() => {
        cy?.resize();
        if (container.value!.clientWidth > 0 && container.value!.clientHeight > 0) {
          // A collapsed <details> has no usable canvas dimensions. Lay out
          // after it becomes visible, not merely fit the hidden layout.
          if (wasHidden) layout();
          wasHidden = false;
        } else wasHidden = true;
        version.value++;
      });
      observer.observe(container.value!);
      sync();
    });
    watch(
      () => [props.nodes, props.edges, props.focus, props.fitRequest],
      () => nextTick(sync),
    );
    onBeforeUnmount(() => {
      observer?.disconnect();
      cy?.destroy();
    });
    const button = (label: string, fn: () => void) => h('button', { type: 'button', onClick: fn }, label);
    return () => {
      void version.value;
      return h('div', { class: 'graph-scene' }, [
        h('div', { class: 'scene-toolbar' }, [
          button('适配画布', () => cy?.fit(undefined, 60)),
          button('放大', () =>
            cy?.zoom({
              level: Math.min(3, cy.zoom() * 1.3),
              renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
            }),
          ),
          button('缩小', () =>
            cy?.zoom({
              level: Math.max(0.03, cy.zoom() / 1.3),
              renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
            }),
          ),
          h('span', Math.round(zoom.value * 100) + '%'),
          button('重新布局', layout),
        ]),
        h('div', {
          ref: container,
          class: 'scene-canvas',
          'data-testid': 'graph-canvas',
          'aria-label': props.trace ? '回答的溯源关系图' : '可交互的材料图谱',
        }),
        !props.nodes.length ? h('div', { class: 'scene-empty' }, '搜索并选择一个实体，从这里开始探索') : null,
        h('details', { class: 'scene-accessibility' }, [
          h('summary', '画布节点与关系 · 键盘导航'),
          h(
            'div',
            props.nodes.map((n) => {
              const point = cy?.getElementById(n.id).renderedPosition();
              return h(
                'button',
                {
                  type: 'button',
                  'data-node-id': n.id,
                  'data-kind': n.kind,
                  'data-x': point?.x,
                  'data-y': point?.y,
                  onClick: () => emit('select', n.id),
                  onDblclick: () => emit('expand', n.id),
                },
                n.label,
              );
            }),
          ),
          h('div', {class:'scene-edge-navigation'}, props.edges.map(e => {
            const id = 'edge:' + JSON.stringify([e.from, e.to, e.kind, e.role || '']);
            const edge = cy?.getElementById(id);
            const point = edge?.length ? (edge as cytoscape.EdgeSingular).renderedMidpoint() : undefined;
            return h('button', {type:'button', 'data-edge-id':id, 'data-x':point?.x, 'data-y':point?.y,
              onClick:()=>emit('edge',e)}, `${short(props.nodes.find(n=>n.id===e.from)?.label || '')} → ${edgeLabel(e)} → ${short(props.nodes.find(n=>n.id===e.to)?.label || '')}`);
          })),
        ]),
      ]);
    };
  },
});
